import React from 'react';
import {
  ServerIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// Helper to safely convert any value to string
const safeString = (val) => {
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (val.name) return String(val.name);
    const keys = Object.keys(val);
    return keys.length > 0 ? String(keys[0]) : 'N/A';
  }
  return 'N/A';
};

const RAGStatisticsCard = ({ ragStats }) => {
  if (!ragStats) {
    return (
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const { knowledge, byDepartment, byType, vectorStore, embedding } = ragStats;

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center">
          <ServerIcon className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            RAG Statistics
          </h3>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Knowledge Base Overview */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Knowledge Base
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {knowledge?.totalDocuments || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Total Documents</p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                <span className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {knowledge?.indexed || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Indexed</p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                  {knowledge?.pending || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Pending</p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                <span className="text-2xl font-bold text-red-900 dark:text-red-100">
                  {knowledge?.failed || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Failed</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Chunks</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {knowledge?.totalChunks?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Chunks per Doc</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {knowledge?.avgChunksPerDoc?.toFixed(1) || 0}
              </p>
            </div>
          </div>
        </div>

        {/* By Department */}
        {byDepartment && byDepartment.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Distribution by Department
            </h4>
            <div className="space-y-2">
              {byDepartment.map((dept, idx) => {
                const deptName = safeString(dept._id);

                return (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-[#222222] rounded">
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {deptName}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {dept.count || 0} docs, {dept.totalChunks || 0} chunks
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-gray-200 dark:bg-[#222222] rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${knowledge?.totalDocuments ? ((dept.count || 0) / knowledge.totalDocuments * 100) : 0}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {dept.indexed || 0}/{dept.count || 0}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* By Content Type */}
        {byType && byType.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              By Content Type
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {byType.map((type, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{safeString(type._id)}</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {type.count || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {type.indexed || 0} indexed
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Embedding Service Stats */}
        {embedding && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Embedding Service
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">Cache Size</p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {embedding.cacheSize || 0}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Cost</p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  ${embedding.totalCost?.toFixed(4) || '0.0000'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Vector Store Info */}
        {vectorStore && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Vector Store (Pinecone)
            </h4>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4">
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">Index Name</dt>
                  <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                    {String(vectorStore.indexName || 'iris-knowledge')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">Dimensions</dt>
                  <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                    {String(vectorStore.dimension || '1536')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">Total Vectors</dt>
                  <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                    {String(vectorStore.totalVectorCount?.toLocaleString() || '0')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">Namespaces</dt>
                  <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                    {String(vectorStore.namespaces || byDepartment?.length || '0')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RAGStatisticsCard;
